import { AlertTriangleIcon } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { useBilling, useKeepZones, useMe, useZones } from '@/api/queries'
import type { OverLimit } from '@/api/types'
import { ApiErrorText } from '@/components/UpgradePrompt'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { daysUntil, keepSelectionValid, overLimitLines } from '@/lib/billing'
import { formatAbsolute, pluralise } from '@/lib/format'
import { useReadOnly } from '@/staff/hooks'

/**
 * Shown on every console page, to account ADMINs only, while the account is above its plan's caps
 * (stripe.md §9). It says what is over, when the grace ends, which zones would be deactivated, and
 * offers the choice of which to keep. Nothing is deleted at the deadline: zones are deactivated and
 * can be reactivated once the account is back under its caps.
 */
export function OverLimitBanner() {
  const me = useMe()
  const isAdmin = me.data?.role === 'ADMIN'
  const billing = useBilling({ enabled: isAdmin })
  const readOnly = useReadOnly()
  const [choosing, setChoosing] = useState(false)

  const over = billing.data?.over_limit
  if (!isAdmin || over === null || over === undefined) return null

  const lines = overLimitLines(over)
  const days = daysUntil(over.deadline)
  return (
    <>
      <Alert variant="warning" className="rounded-none border-x-0 border-t-0 px-6 lg:px-10" data-testid="over-limit-banner">
        <AlertTriangleIcon />
        <AlertTitle>Your account is over its {billing.data?.plan_name ?? 'plan'} limits</AlertTitle>
        <AlertDescription className="flex flex-col gap-2">
          <ul className="list-disc pl-5">
            {lines.map((line) => (
              <li key={line.label}>
                {line.label}: {line.inUse} in use, {line.limit} allowed
              </li>
            ))}
          </ul>
          <p>
            Grace ends {formatAbsolute(over.deadline)}
            {days === null ? '' : ` (${pluralise(days, 'day')} left)`}. Then the excess is deactivated
            (never deleted).
            {over.would_deactivate.length === 0 ? null : (
              <>
                {' '}
                Zones that would be deactivated:{' '}
                <strong data-testid="over-limit-zones">
                  {over.would_deactivate.map((zone) => zone.name).join(', ')}
                </strong>
                .
              </>
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm">
              <Link to="/app/plan">Upgrade</Link>
            </Button>
            {over.zones?.over === true && !readOnly ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setChoosing(true)
                }}
              >
                Choose zones to keep
              </Button>
            ) : null}
          </div>
        </AlertDescription>
      </Alert>
      {choosing ? (
        <KeepZonesDialog
          over={over}
          onClose={() => {
            setChoosing(false)
          }}
        />
      ) : null}
    </>
  )
}

function KeepZonesDialog({ over, onClose }: { over: OverLimit; onClose: () => void }) {
  const zones = useZones('active')
  const keep = useKeepZones()
  const limit = over.zones?.limit ?? 1
  const doomed = new Set(over.would_deactivate.map((zone) => zone.zone_id))
  const [selected, setSelected] = useState<string[]>(() =>
    over.keep_zone_ids.length > 0 ? over.keep_zone_ids : [],
  )

  // Until the admin chooses, the server's own default (everything not in would_deactivate) stays.
  const effective =
    selected.length > 0
      ? selected
      : (zones.data ?? []).filter((zone) => !doomed.has(zone.id)).map((zone) => zone.id)

  function toggle(id: string) {
    setSelected(effective.includes(id) ? effective.filter((other) => other !== id) : [...effective, id])
  }

  const valid = keepSelectionValid(effective, limit)
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !keep.isPending) onClose()
      }}
    >
      <DialogContent data-testid="keep-zones-dialog">
        <DialogHeader>
          <DialogTitle>Choose zones to keep</DialogTitle>
          <DialogDescription>
            Your plan allows {pluralise(limit, 'zone')}. The zones you do not keep are deactivated at
            the deadline, not deleted. The zone named <span className="font-mono">main</span> is never
            deactivated.
          </DialogDescription>
        </DialogHeader>
        <ul className="flex max-h-[50dvh] min-h-0 flex-col gap-2 overflow-y-auto">
          {(zones.data ?? []).map((zone) => (
            <li key={zone.id}>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={effective.includes(zone.id)}
                  onChange={() => {
                    toggle(zone.id)
                  }}
                />
                <span className="min-w-0 truncate">{zone.name}</span>
              </label>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          {effective.length} of {limit} selected.
        </p>
        {keep.isError ? (
          <Alert variant="destructive">
            <AlertDescription>
              <ApiErrorText error={keep.error} />
            </AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" disabled={keep.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!valid || keep.isPending}
            onClick={() => {
              keep.mutate(effective, { onSuccess: onClose })
            }}
          >
            {keep.isPending ? 'Saving…' : 'Keep these zones'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
